/**
 * HW Card Variant Picker
 * Coordinates hw-size-picker with hw-swatches on product cards.
 *
 * Flow:
 *   - Color swatch change → SwatchesVariantPickerComponent (product-card.js) stores color
 *     in card state and dispatches hw:card-option-changed (no variant yet).
 *   - HwSizePicker._onOptionChanged → updates tile availability → re-selects best size
 *     → dispatches hw:card-option-changed WITH resolved variant.
 *   - ProductCard.#handleHwOptionChanged (product-card.js) → updates price + gallery image.
 *
 *   - Size tile click → HwSizePicker stores size in card state → dispatches
 *     hw:card-option-changed WITH resolved variant → ProductCard updates price + image.
 *
 * Card state is stored as JSON on product-card[data-hw-options]:
 *   { "1": "Red", "2": "M" }  (option position → selected value name)
 */

const HW_OPTION_CHANGED = 'hw:card-option-changed';

class HwSizePicker extends HTMLElement {
  connectedCallback() {
    // Defer so the HTML parser finishes stamping children (tiles + variant JSON
    // script tag) before we try to read them. connectedCallback fires on the
    // opening tag, before child nodes exist in parser-connected elements.
    setTimeout(() => this._init(), 0);
  }

  _init() {
    this._variants = this._readVariants();
    this._optionPosition = parseInt(this.dataset.optionPosition, 10);

    const card = this.closest('product-card');
    if (!card) return;

    // Seed card state from any swatch radios already checked in the DOM
    this._seedCardStateFromSwatches(card);

    // Listen for option changes from sibling pickers (e.g., color swatch)
    card.addEventListener(HW_OPTION_CHANGED, this._onOptionChanged);

    // Handle tile clicks
    this.addEventListener('click', this._onTileClick);

    // Initialize: update availability then pre-select first available size
    const otherSelections = this._getOtherSelections(card);
    this._updateAvailability(otherSelections);
    this._initSelection(card, otherSelections);
  }

  disconnectedCallback() {
    this.closest('product-card')?.removeEventListener(HW_OPTION_CHANGED, this._onOptionChanged);
  }

  // ─── Variant JSON ───────────────────────────────────────────────────────────

  _readVariants() {
    const script = this.querySelector('script[data-hw-variants]');
    if (!script) return [];
    const raw = script.textContent ?? '';
    if (!raw.trim()) return [];
    try {
      return JSON.parse(raw.trim());
    } catch {
      return [];
    }
  }

  // ─── Card state helpers ──────────────────────────────────────────────────────

  _getCardState(card) {
    try {
      return JSON.parse(card.dataset.hwOptions || '{}');
    } catch {
      return {};
    }
  }

  /** Returns all card state entries EXCEPT our own option position. */
  _getOtherSelections(card) {
    const state = this._getCardState(card);
    const result = {};
    for (const [pos, val] of Object.entries(state)) {
      if (parseInt(pos, 10) !== this._optionPosition) result[pos] = val;
    }
    return result;
  }

  /**
   * On page load, read any swatch radios that Liquid pre-checked and store
   * their option positions + values into card state so we can use them for
   * initial availability filtering before any user interaction.
   */
  _seedCardStateFromSwatches(card) {
    const checkedInputs = card.querySelectorAll('input[type="radio"][name*="-swatch"]:checked');
    if (!checkedInputs.length) return;

    let state = this._getCardState(card);
    for (const input of checkedInputs) {
      const inputId = input.dataset.inputId || '';
      const optionPosition = parseInt(inputId.split('-')[0], 10);
      if (!isNaN(optionPosition) && input.value) {
        state[optionPosition] = input.value;
      }
    }
    card.dataset.hwOptions = JSON.stringify(state);
  }

  // ─── Variant resolution ──────────────────────────────────────────────────────

  /**
   * Finds the best variant matching sizeValue + otherSelections.
   * Prefers available variants; falls back to any matching variant.
   */
  _findBestVariant(sizeValue, otherSelections) {
    const pos = this._optionPosition - 1; // 0-indexed into options array

    // Pass 1: available + all selections match
    for (const v of this._variants) {
      if (v.options[pos] !== sizeValue || !v.available) continue;
      if (this._matchesSelections(v, otherSelections)) return v;
    }

    // Pass 2: unavailable but all selections match (for display purposes)
    for (const v of this._variants) {
      if (v.options[pos] !== sizeValue) continue;
      if (this._matchesSelections(v, otherSelections)) return v;
    }

    return null;
  }

  _matchesSelections(variant, selections) {
    for (const [posStr, val] of Object.entries(selections)) {
      const idx = parseInt(posStr, 10) - 1;
      if (variant.options[idx] !== val) return false;
    }
    return true;
  }

  // ─── Tile availability ───────────────────────────────────────────────────────

  /**
   * Marks tiles as available/unavailable based on otherSelections (e.g., current color).
   * Option 1 behavior: all tiles visible, unavailable ones greyed + strikethrough.
   */
  _updateAvailability(otherSelections) {
    for (const tile of this.querySelectorAll('.hw-size-picker-block__tile')) {
      const value = tile.dataset.optionValue;
      const variant = this._findBestVariant(value, otherSelections);
      const available = variant?.available ?? false;

      tile.classList.toggle('hw-size-picker-block__tile--unavailable', !available);
      if (available) {
        tile.removeAttribute('aria-disabled');
      } else {
        tile.setAttribute('aria-disabled', 'true');
      }
    }
  }

  // ─── Selection logic ─────────────────────────────────────────────────────────

  /**
   * On init: restore previously stored selection, or pick first available.
   */
  _initSelection(card, otherSelections) {
    const state = this._getCardState(card);
    const stored = state[this._optionPosition];

    if (stored) {
      const tile = this._getTileByValue(stored);
      if (tile) {
        this._markSelected(tile);
        return; // Card display was already correct from server render
      }
    }

    // No stored selection — find and activate first available tile
    this._selectFirstAvailable(card, otherSelections);
  }

  _selectFirstAvailable(card, otherSelections) {
    for (const tile of this.querySelectorAll('.hw-size-picker-block__tile')) {
      if (tile.classList.contains('hw-size-picker-block__tile--unavailable')) continue;
      this._markSelected(tile);
      this._applySelection(tile.dataset.optionValue, card, otherSelections, false);
      break;
    }
  }

  _applySelection(value, card, otherSelections, userInitiated = false) {
    const variant = this._findBestVariant(value, otherSelections);
    if (!variant) return;

    // Write to card state
    const state = this._getCardState(card);
    state[this._optionPosition] = value;
    card.dataset.hwOptions = JSON.stringify(state);

    // Notify card (ProductCard will update price + image)
    card.dispatchEvent(
      new CustomEvent(HW_OPTION_CHANGED, {
        bubbles: false,
        detail: { optionPosition: this._optionPosition, value, variant, userInitiated },
      })
    );
  }

  _markSelected(tile) {
    for (const t of this.querySelectorAll('.hw-size-picker-block__tile')) {
      t.classList.remove('hw-size-picker-block__tile--selected');
      t.setAttribute('aria-pressed', 'false');
    }
    tile.classList.add('hw-size-picker-block__tile--selected');
    tile.setAttribute('aria-pressed', 'true');
  }

  _getTileByValue(value) {
    return this.querySelector(`.hw-size-picker-block__tile[data-option-value="${CSS.escape(value)}"]`);
  }

  // ─── Event handlers ──────────────────────────────────────────────────────────

  /** Fires when another option (e.g., color swatch) changes. */
  _onOptionChanged = (event) => {
    const { optionPosition } = event.detail;
    if (optionPosition === this._optionPosition) return; // ignore own events

    const card = this.closest('product-card');
    if (!card) return;

    const otherSelections = this._getOtherSelections(card);
    this._updateAvailability(otherSelections);

    // Re-evaluate current selection against new context
    const state = this._getCardState(card);
    const currentValue = state[this._optionPosition];

    if (currentValue) {
      const variant = this._findBestVariant(currentValue, otherSelections);
      if (variant?.available) {
        // Current selection is still valid — dispatch updated variant (price may differ)
        card.dispatchEvent(
          new CustomEvent(HW_OPTION_CHANGED, {
            bubbles: false,
            detail: { optionPosition: this._optionPosition, value: currentValue, variant, userInitiated: true },
          })
        );
        return;
      }
    }

    // Current selection unavailable or none — pick first available
    this._selectFirstAvailable(card, otherSelections);
  };

  /** Fires when a size tile is clicked. */
  _onTileClick = (event) => {
    const tile = event.target.closest('.hw-size-picker-block__tile');
    if (!tile) return;
    if (tile.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    event.stopPropagation();

    const card = this.closest('product-card');
    if (!card) return;

    this._markSelected(tile);
    this._applySelection(tile.dataset.optionValue, card, this._getOtherSelections(card), true);
  };
}

if (!customElements.get('hw-size-picker')) {
  customElements.define('hw-size-picker', HwSizePicker);
}
